/* eslint-disable no-script-url */

import React from 'react';
import PropTypes from 'prop-types';
import { Dropdown, MenuItem, OverlayTrigger } from 'react-bootstrap';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import PopoverStickOnHover from '@/shared/components/PopoverStickOnHover';
import intlShape from '@/shared/utils/intlShape';
import FormattedListValue, { formatListValue } from '@/shared/components/FormattedListValue';
import { WindowIcon, ClockIcon, PaperclipIcon, CommentIcon } from '@/shared/components/Icons';
import HoverToggle from '@/shared/components/HoverToggle';
import handleHttpError from '@/shared/services/HandleHttpError';
import api from '@/shared/api';
import config from '@/config';
import classNames from 'classnames';
import AuthService, { functionalities, privileges } from '@/shared/services/AuthService';
import TaskAttachmentsPopover from './TaskAttachmentsPopover';
import TaskAttachmentIcon from './TaskAttachmentIcon';
import messages from './taskMessages';
import TaskStatusIcon from './TaskStatusIcon';
import TaskRemarkIcon from './TaskRemarkIcon';

const TaskStatusLabel = ({ status, isBlocked, inapplicable, rejected }) => {
  const intl = useIntl();
  if (inapplicable) return intl.formatMessage(messages.Inapplicable);
  if (isBlocked) return intl.formatMessage(messages.Blocked);
  if (rejected) return formatListValue({ id: 6, list: 'extendedtaskstatus', intl });
  return formatListValue({ id: status, list: 'taskstatus', intl });
};
const changeStatusTitleMessages = [
  messages.ChangeMainTaskStatus,
  messages.ChangeSubtaskStatus,
  messages.ChangeTodoStatus,
];

export const getNextStatus = value => {
  if (!value) return null;
  const { status, isBlocked, inapplicable, rejected } = value;
  if (inapplicable || rejected) return null;
  if (isBlocked) return { ...value, isBlocked: false };
  if (status === 2) return null;
  if (status === 0) return { ...value, status: 1 };
  if (status === 1) return { ...value, status: 2 };
  return null;
};

class TaskStatusToggle extends React.Component {
  state = { isDropdownOpen: false };

  constructor(props) {
    super(props);
    const {
      value: { taskLevel },
    } = props;

    const functionality = taskLevel === 2 ? functionalities.Todo : functionalities.PlannedTask;
    this.canEdit = AuthService.hasPermission({ functionality, privilege: privileges.Update });
    this.canCreateRegistration = AuthService.hasPermission({
      functionality: functionalities.RegistrationOwn,
      privilege: privileges.Create,
    });
    this.canDeleteTodo = AuthService.hasPermission({
      functionality: functionalities.Todo,
      privilege: privileges.Delete,
    });
  }

  changeStatus = changes => {
    const { value } = this.props;
    const newStatus = { ...value, ...changes };
    if (!value.id) return;
    if (JSON.stringify(newStatus) === JSON.stringify(value)) return;
    this.setState({ saving: true });

    return api.base
      .put(`${config.appUrls.tasks}/api/TaskList/UpdateStatus`, {
        status: newStatus.status,
        isBlocked: newStatus.isBlocked,
        rejected: newStatus.rejected,
        inapplicable: newStatus.inapplicable,
        id: value.id,
        taskLevel: newStatus.taskLevel,
      })
      .then(
        ({ data }) => {
          this.setState({ saving: false, isDropdownOpen: false });
          const { onTasksUpdated } = this.props;
          if (data && data.length > 0 && onTasksUpdated && typeof onTasksUpdated === 'function') {
            onTasksUpdated(data);
          }
          return data;
        },
        error => {
          const { intl } = this.props;
          this.setState({ saving: false });
          handleHttpError(intl)(error);
        },
      );
  };

  toggleDropdown = isOpen => {
    this.setState({ isDropdownOpen: isOpen });
  };

  render() {
    const {
      id,
      value,
      overlay,
      intl,
      isMainTask,
      title,
      showStatusLabel,
      onOpenDetail,
      onAddRegistration,
      showDeleteTask,
      onDeleteTask,
      extraInfo,
      pullRight,
    } = this.props;
    const { saving, isDropdownOpen } = this.state;
    const { status, isBlocked, inapplicable, rejected, taskLevel } = value;
    // To-dos have no inapplicable/rejected state — those only exist on planned main/subtasks.
    const isTodo = taskLevel === 2;
    const canBlock = !isMainTask && !inapplicable && !isBlocked;
    const canSetInapplicable = !isTodo && !inapplicable;
    const canSetRejected = !isTodo && !rejected;
    // For a to-do this group is empty, and its divider would end up right on top of the next one.
    const hasStatusFlagItems = isBlocked || canBlock || canSetInapplicable || canSetRejected;

    const nextStatus = getNextStatus(value);
    const iconProps = {
      status,
      isBlocked,
      inapplicable,
      rejected,
    };
    if (nextStatus !== null && this.canEdit) {
      iconProps.onClick = () => this.changeStatus(nextStatus);
    }

    const statusTitle =
      title ||
      (this.canEdit
        ? (Number.isNaN(taskLevel)
            ? null
            : intl.formatMessage(changeStatusTitleMessages[taskLevel])) ||
          intl.formatMessage(messages.ChangeTaskStatus)
        : null);

    return (
      <div className="task-status-wrapper" style={{ display: 'flex' }}>
        <TaskAttachmentIcon
          taskId={value.id}
          taskLevel={value.taskLevel}
          hasAttachments={value.hasAttachments}
          onOpenDetail={onOpenDetail}
          intl={intl}
        />
        <TaskRemarkIcon
          taskId={value.id}
          taskLevel={value.taskLevel}
          hasRemark={value.hasRemark}
          changedCounter={value.changedCounter || 0}
          scheduleHasRemark={value.scheduleHasRemark}
          onOpenDetail={onOpenDetail}
          intl={intl}
        />
        <div className="task-status-toggle">
          <Dropdown
            id={id}
            className="TaskStatusToggle"
            open={isDropdownOpen}
            onToggle={this.toggleDropdown}
            pullRight={pullRight}
            onClick={e => {
              e.stopPropagation();
            }}
          >
            {overlay ? (
              <OverlayTrigger
                rootClose
                placement="bottom"
                overlay={overlay}
                delayShow={150}
                delayHide={150}
              >
                <span>
                  {saving && <i className="fa fa-spin fa-spinner" />}
                  {!saving && <TaskStatusIcon {...iconProps} />}
                  {showStatusLabel && status !== null && (
                    <span className="margin5L">
                      <TaskStatusLabel {...iconProps} />
                    </span>
                  )}
                  {extraInfo}
                </span>
              </OverlayTrigger>
            ) : (
              <span>
                <TaskStatusIcon {...iconProps} />
                {extraInfo}
              </span>
            )}

            <HoverToggle bsRole="toggle" title={statusTitle}>
              {showStatusLabel && status !== null && (
                <span className="margin5L">
                  <TaskStatusLabel {...iconProps} />
                </span>
              )}
            </HoverToggle>
            <Dropdown.Menu className="unselectable">
              {isDropdownOpen ? (
                <>
                  {statusTitle ? <MenuItem header>{statusTitle}</MenuItem> : null}
                  {this.canEdit && (
                    <MenuItem
                      eventKey="0"
                      onSelect={() =>
                        this.changeStatus({
                          status: 0,
                          isBlocked: false,
                          inapplicable: false,
                          rejected: false,
                        })
                      }
                    >
                      <i className="fa fa-lg fa-circle greyed-out margin5R" />
                      <FormattedListValue list="taskstatus" id={0} fallback="Todo" />
                    </MenuItem>
                  )}

                  {this.canEdit && value.taskType !== 2 && value.type !== 2 && (
                    <MenuItem
                      eventKey="1"
                      onSelect={() =>
                        this.changeStatus({
                          status: 1,
                          isBlocked: false,
                          inapplicable: false,
                          rejected: false,
                        })
                      }
                    >
                      <i className="fa fa-lg fa-circle orange margin5R" />
                      <FormattedListValue list="taskstatus" id={1} fallback="In progress" />
                    </MenuItem>
                  )}
                  {this.canEdit && (
                    <MenuItem
                      eventKey="2"
                      onSelect={() =>
                        this.changeStatus({
                          status: 2,
                          isBlocked: false,
                          inapplicable: false,
                          rejected: false,
                        })
                      }
                    >
                      <i className="fa fa-lg fa-circle green margin5R" />
                      <FormattedListValue list="taskstatus" id={2} fallback="Done" />
                    </MenuItem>
                  )}
                  {this.canEdit && !inapplicable && !rejected && hasStatusFlagItems && (
                    <MenuItem divider />
                  )}
                  {this.canEdit && isBlocked && (
                    <MenuItem eventKey="3" onSelect={() => this.changeStatus({ isBlocked: false })}>
                      <i className="fa fa-lg fa-arrow-circle-up text-primary margin5R" />
                      <FormattedMessage {...messages.Unblock} />
                    </MenuItem>
                  )}
                  {this.canEdit && canBlock && (
                    <MenuItem eventKey="3" onSelect={() => this.changeStatus({ isBlocked: true })}>
                      <i className="fa fa-lg fa-minus-circle text-danger margin5R" />
                      <FormattedMessage {...messages.Blocked} />
                    </MenuItem>
                  )}
                  {this.canEdit && canSetInapplicable && (
                    <MenuItem
                      eventKey="4"
                      onSelect={() => this.changeStatus({ inapplicable: true })}
                    >
                      <i className="fa fa-lg fa-times-circle margin5R" />
                      <FormattedMessage {...messages.Inapplicable} />
                    </MenuItem>
                  )}
                  {this.canEdit && canSetRejected && (
                    <MenuItem eventKey="5" onSelect={() => this.changeStatus({ rejected: true })}>
                      <i className="fa fa-lg fa-ban margin5R" />
                      <FormattedMessage {...messages.Rejected} />
                    </MenuItem>
                  )}
                  {this.canEdit && (onOpenDetail || onAddRegistration) ? (
                    <MenuItem divider />
                  ) : null}
                  {!!onOpenDetail && (
                    <MenuItem eventKey="9" onSelect={() => onOpenDetail(value.id)}>
                      <WindowIcon
                        className="fa-fw margin5R"
                        tooltip={intl.formatMessage(messages.OpenTaskDetails)}
                      />
                      <FormattedMessage {...messages.OpenTaskDetails} />
                    </MenuItem>
                  )}
                  {!!onAddRegistration &&
                    this.canCreateRegistration &&
                    value.taskType !== 2 &&
                    value.type !== 2 && (
                      <MenuItem eventKey="10" onSelect={() => onAddRegistration(value)}>
                        <ClockIcon
                          className="margin5R"
                          clickable
                          tooltip={intl.formatMessage(messages.AddRegistration)}
                        />
                        <FormattedMessage {...messages.AddRegistration} />
                      </MenuItem>
                    )}
                  {showDeleteTask && isTodo && this.canDeleteTodo && !!onDeleteTask && (
                    <>
                      <MenuItem eventKey="11" onSelect={() => onDeleteTask(value)}>
                        <i className="fa fa-lg fa-trash-o text-danger margin5R" />
                        <FormattedMessage {...messages.DeleteTodo} />
                      </MenuItem>
                    </>
                  )}
                </>
              ) : null}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    );
  }
}

TaskStatusToggle.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.oneOf([undefined, 0, 1, 2]),
    isBlocked: PropTypes.bool,
    inapplicable: PropTypes.bool,
    rejected: PropTypes.bool,
    hasAttachments: PropTypes.bool,
    hasRemark: PropTypes.bool,
    taskLevel: PropTypes.number,
    scheduleHasRemark: PropTypes.bool,
    taskType: PropTypes.number,
  }),
  overlay: PropTypes.element,
  isMainTask: PropTypes.bool,
  showStatusLabel: PropTypes.bool,
  intl: intlShape.isRequired,
  extraInfo: PropTypes.node, // Show extra info after icon and label
  title: PropTypes.string,
  onOpenDetail: PropTypes.func,
  onTasksUpdated: PropTypes.func,
  onAddRegistration: PropTypes.func,
  showDeleteTask: PropTypes.bool,
  onDeleteTask: PropTypes.func,
  pullRight: PropTypes.bool,
};
TaskStatusToggle.defaultProps = {
  value: {
    status: null,
    isBlocked: false,
    inapplicable: false,
    rejected: false,
    hasAttachments: undefined,
    hasRemark: undefined,
    taskLevel: null,
    scheduleHasRemark: false,
    taskType: null,
  },
  title: null,
  overlay: null,
  isMainTask: false,
  showStatusLabel: false,
  onTasksUpdated: null,
  onOpenDetail: null,
  onAddRegistration: null,
  showDeleteTask: false,
  onDeleteTask: null,
  extraInfo: null,
  pullRight: false,
};

export default injectIntl(TaskStatusToggle);
